import React, { useState } from 'react';
import { AlertTriangle, Copy, Lock, Share2, Webhook } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../lib/clipboard';
import { publicBaseUrl } from '../../lib/developerApi';
import { Method, Note, ScopeChip, StatusCode } from './parts';

/**
 * Static reference for integrators, mirroring docs/api/runtime/public-v1.md —
 * the canonical contract. When the API changes, change that file first and this
 * view with it; nothing here is fetched from the server.
 */
export const ENDPOINTS = [
    {
        id: 'me', group: 'Getting started', method: 'GET', path: '/me', scopes: [],
        description: 'Check that a key works. Returns the organization, the key and its permissions.',
        response: '{\n  "object": "api_key",\n  "organization": { "id": "org_…", "name": "Harbourline" },\n  "key": { "id": "Vq7Lm2Rt", "name": "Velocity" },\n  "scopes": ["contacts:read", "contacts:create", "users:read"]\n}',
    },
    {
        id: 'contacts-list', group: 'Contacts', method: 'GET', path: '/contacts', scopes: ['contacts:read'],
        description: 'List contacts, ordered by id. Page with next_cursor; use updated_since for incremental sync.',
        params: [
            ['limit', 'integer', '1–100, default 50'],
            ['cursor', 'string', 'The previous page’s next_cursor'],
            ['email', 'string', 'Exact match, case-insensitive'],
            ['referred_by', 'string', 'A teammate’s Referral ID, email or user id'],
            ['updated_since', 'ISO 8601', 'Only records changed at or after this time'],
        ],
        response: '{\n  "object": "list",\n  "data": [ { "object": "contact", "id": "4f0c…", "email": "dana@northwind.io", … } ],\n  "next_cursor": "eyJhZnRlciI6…"\n}',
    },
    {
        id: 'contacts-create', group: 'Contacts', method: 'POST', path: '/contacts', scopes: ['contacts:create'],
        description: 'Create a contact. Send an Idempotency-Key so a retry never creates a duplicate. attribution.referred_by — a teammate’s Referral ID or email — credits them and makes them the owner. An email that already has a contact answers 409 contact_exists with its id; use upsert when your app may send the same person twice.',
        body: '{\n  "email": "dana@northwind.io",\n  "first_name": "Dana",\n  "last_name": "Reyes",\n  "tags": ["webinar-oct"],\n  "attribution": { "referred_by": "owen-banan", "campaign": "oct-webinar" }\n}',
        response: '{\n  "object": "contact", "id": "4f0c…", "created": true,\n  "email": "dana@northwind.io",\n  "owner": { "id": "usr_7f2…", "name": "Owen Banan", "reason": "referrer" },\n  "attribution": { "status": "resolved",\n    "referred_by": { "id": "usr_7f2…", "name": "Owen Banan", "referral_id": "owen-banan" },\n    "source": "Velocity", "first_touch": true },\n  "warnings": []\n}',
    },
    {
        id: 'contacts-upsert', group: 'Contacts', method: 'POST', path: '/contacts/upsert', scopes: ['contacts:create', 'contacts:update'],
        description: 'Create the contact, or update the one with the same email. Tags are added and custom fields merged, never wiped; the owner never changes. company links (or, with companies:create, creates) the company. If no teammate matches attribution.referred_by, the lead still goes to the key’s default owner, with a warning.',
        body: '{\n  "email": "kim.aldous@harbourline.com",\n  "first_name": "Kim",\n  "company": { "name": "Harbourline", "website": "harbourline.com" },\n  "attribution": { "referred_by": "owen-banan" }\n}',
        response: '{\n  "object": "contact", "id": "5k1…", "created": false,\n  "owner": { "id": "usr_7f2…", "name": "Owen Banan", "reason": "existing_owner" },\n  "attribution": { "status": "resolved", "first_touch": false, … },\n  "company": { "id": "9d3…", "name": "Harbourline", "created": false },\n  "warnings": []\n}',
    },
    {
        id: 'contacts-patch', group: 'Contacts', method: 'PATCH', path: '/contacts/{id}', scopes: ['contacts:update'],
        description: 'Update only the fields you send; values replace what is stored. Unknown fields are rejected with 422, never ignored. attribution is not accepted here — only an admin can correct a credit.',
        body: '{\n  "title": "Chief Revenue Officer",\n  "tags": ["webinar-oct", "hot"]\n}',
        response: '{ "object": "contact", "id": "4f0c…", "title": "Chief Revenue Officer", … }',
    },
    {
        id: 'contacts-events', group: 'Contacts', method: 'POST', path: '/contacts/{id}/events', scopes: ['events:create'],
        description: 'Put something that happened in your app on the contact’s timeline — a certification, a sign-up, a purchase. Any lower-case event name; properties is a small JSON object (50 keys, 3 levels, 8 KB).',
        body: '{\n  "name": "certification.completed",\n  "occurred_at": "2026-09-30T15:20:00Z",\n  "properties": { "level": "gold", "score": 92 }\n}',
        response: '{\n  "object": "event", "id": "a81…", "contact_id": "4f0c…",\n  "name": "certification.completed", "source": "Velocity"\n}',
    },
    {
        id: 'contacts-delete', group: 'Contacts', method: 'DELETE', path: '/contacts/{id}', scopes: ['contacts:delete'],
        description: 'Soft-delete, exactly as deleting in the CRM does. The email becomes free for a new contact.',
        response: '{ "object": "contact", "id": "4f0c…", "deleted": true }',
    },
    {
        id: 'companies-list', group: 'Companies', method: 'GET', path: '/companies', scopes: ['companies:read'],
        description: 'List companies. Same paging as contacts, plus a website filter (matched by domain).',
        params: [['limit', 'integer', '1–100'], ['cursor', 'string', 'next_cursor'], ['website', 'string', 'e.g. northwind.io'], ['updated_since', 'ISO 8601', '']],
        response: '{ "object": "list", "data": [ { "object": "company", "id": "co…", "name": "Northwind", "domain": "northwind.io" } ], "next_cursor": null }',
    },
    {
        id: 'companies-upsert', group: 'Companies', method: 'POST', path: '/companies/upsert', scopes: ['companies:create', 'companies:update'],
        description: 'Create the company, or update the one with the same website domain (https, www. and paths are ignored).',
        body: '{\n  "name": "Northwind",\n  "website": "northwind.io",\n  "industry": "Logistics"\n}',
        response: '{ "object": "company", "id": "co…", "created": false, … }',
    },
    {
        id: 'companies-delete', group: 'Companies', method: 'DELETE', path: '/companies/{id}', scopes: ['companies:delete'],
        description: 'Soft-delete. Contacts and deals keep their link, so restoring the company in the CRM restores everything.',
        response: '{ "object": "company", "id": "co…", "deleted": true }',
    },
    {
        id: 'users-list', group: 'Team members', method: 'GET', path: '/users', scopes: ['users:read'],
        description: 'Active teammates only, with exactly id, name, email, referral_id and status — never roles, phones or settings.',
        params: [['limit', 'integer', '1–100'], ['cursor', 'string', 'next_cursor']],
        response: '{ "object": "list", "data": [ { "object": "user", "id": "usr_7f2…", "name": "Owen Banan",\n  "email": "owen@harbourline.com", "referral_id": "owen-banan", "status": "active" } ], "next_cursor": null }',
    },
    {
        id: 'users-lookup', group: 'Team members', method: 'GET', path: '/users/lookup?email=owen%40harbourline.com', scopes: ['users:read'],
        description: 'Find one active teammate by email or Referral ID (older IDs resolve too). 404 when no one matches.',
        params: [['email', 'string', 'Exact, case-insensitive'], ['referral_id', 'string', 'A current or older Referral ID']],
        response: '{ "object": "user", "id": "usr_7f2…", "name": "Owen Banan", "referral_id": "owen-banan", "status": "active" }',
    },
];

const ERRORS = [
    [400, 'invalid_request', 'The body is not a JSON object, or a header is malformed.'],
    [401, 'invalid_key', 'The key is missing, wrong, revoked, expired, or used from outside its IP allowlist.'],
    [403, 'missing_scope', 'The key is valid but lacks the permission this endpoint needs.'],
    [403, 'plan_limit_reached', 'The plan’s contact or company limit is full.'],
    [403, 'forbidden', 'Refused for a reason other than a missing permission.'],
    [404, 'not_found', 'No such endpoint, or no such record in this organization.'],
    [409, 'contact_exists', 'POST /contacts for an email that already has a contact. The error includes contact_id.'],
    [409, 'company_exists', 'A company with this website’s domain exists. The error includes company_id.'],
    [405, 'method_not_allowed', 'The path exists but not with this method.'],
    [409, 'conflict', 'The record changed while it was being written. Retry the request.'],
    [409, 'idempotency_mismatch', 'The Idempotency-Key was reused with a different request.'],
    [409, 'idempotency_in_progress', 'A request with this Idempotency-Key is still running. Retry after Retry-After.'],
    [422, 'invalid_field', 'A field is unknown or its value is invalid. field names the path; the value is never echoed.'],
    [429, 'rate_limited', 'Over the key’s per-minute limit, or too many failed keys from this address. Wait Retry-After seconds.'],
    [500, 'internal_error', 'Our fault. Retry with the same Idempotency-Key; quote the request_id.'],
    [503, 'public_api_disabled', 'The API is switched off for this workspace.'],
];

const WEBHOOK_EVENTS = [
    ['contact.created', 'A contact is created, by anyone or anything'],
    ['contact.updated', 'A contact changes, including its owner'],
    ['contact.deleted', 'A contact is deleted'],
    ['contact.referred', 'A contact is credited to a teammate'],
    ['company.created', 'A company is created through the API'],
    ['company.updated', 'A company is changed through the API'],
    ['company.deleted', 'A company is deleted through the API'],
    ['user.created', 'A teammate joins the organization'],
    ['user.updated', 'A teammate’s name or Referral ID changes'],
    ['user.role_changed', 'A teammate’s role changes'],
    ['user.deactivated', 'A teammate is removed or suspended'],
    ['webhook.test', 'An admin sends a test event'],
];

const WEBHOOK_PAYLOAD = `{
  "id": "evt_4c1f0e6b8a2d7f3e9b5a1c0d",
  "type": "contact.referred",
  "created_at": "2026-10-01T08:12:44.120000+00:00",
  "data": {
    "contact": { "object": "contact", "id": "4f0c…", "email": "kim.aldous@harbourline.com" },
    "referred_by": { "id": "usr_7f2…", "name": "Owen Banan", "referral_id": "owen-banan" }
  }
}`;

const WEBHOOK_VERIFY = `import hashlib, hmac, time

def verify_nurturecrm_signature(header: str, body: bytes, secret: str, tolerance: int = 300) -> bool:
    parts = [p.split("=", 1) for p in header.split(",") if "=" in p]
    timestamp = next((v for k, v in parts if k.strip() == "t"), "")
    signatures = [v for k, v in parts if k.strip() == "v1"]
    if not timestamp.isdigit() or not signatures:
        return False
    if abs(time.time() - int(timestamp)) > tolerance:
        return False  # too old: a replay
    expected = hmac.new(secret.encode(), timestamp.encode() + b"." + body, hashlib.sha256).hexdigest()
    return any(hmac.compare_digest(expected, s) for s in signatures)`;

function WebhookEvents() {
    return (
        <div className="border border-stone-200 rounded-xl p-5 flex flex-col gap-3.5" data-testid="docs-webhooks">
            <b className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Webhook events</b>
            <p className="m-0 text-sm text-stone-600 max-w-[72ch]">
                Admins add endpoints on the Webhooks tab. Each event is a signed JSON POST, retried after 1 min, 5 min, 30 min, 2 h, 12 h and 24 h when your endpoint does not answer 2xx. An endpoint failing for 24 hours is paused; its events wait for 7 days.
            </p>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead><tr className="text-left text-xs font-medium tracking-wider uppercase text-stone-500 border-b border-stone-200"><th className="px-3 py-2">Event</th><th className="px-3 py-2">Sent when</th></tr></thead>
                    <tbody>
                        {WEBHOOK_EVENTS.map(([type, when]) => (
                            <tr key={type} className="border-b border-stone-100 last:border-b-0"><td className="px-3 py-2 font-mono text-xs">{type}</td><td className="px-3 py-2 text-[13px]">{when}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <b className="text-sm">Example payload</b>
            <Code copyText={WEBHOOK_PAYLOAD}>{WEBHOOK_PAYLOAD}</Code>
            <p className="m-0 text-[13px] text-stone-600">
                <span className="font-mono">contact</span>, <span className="font-mono">company</span> and <span className="font-mono">user</span> are the same objects the API returns. Headers: <span className="font-mono">X-NurtureCRM-Event</span>, <span className="font-mono">X-NurtureCRM-Delivery</span> (the same on every retry) and <span className="font-mono">X-NurtureCRM-Signature</span>. Events can arrive out of order: order by <span className="font-mono">created_at</span> and use <span className="font-mono">id</span> to drop duplicates.
            </p>
            <b className="text-sm">Verifying the signature</b>
            <p className="m-0 text-[13px] text-stone-600">
                <span className="font-mono">X-NurtureCRM-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</span> is an HMAC-SHA256 of <span className="font-mono">{'"{t}.{raw body}"'}</span> keyed with your endpoint’s signing secret. Verify the raw bytes, and reject timestamps older than 5 minutes. Right after a rotation the header carries two <span className="font-mono">v1</span> values for 24 hours; accept either.
            </p>
            <Code copyText={WEBHOOK_VERIFY}>{WEBHOOK_VERIFY}</Code>
        </div>
    );
}

function codeFor(endpoint, lang) {
    const url = publicBaseUrl() + endpoint.path.replace('{id}', '4f0c…');
    const writes = endpoint.method !== 'GET';
    if (lang === 'curl') {
        let s = `curl -X ${endpoint.method} "${url}" \\\n  -H "Authorization: Bearer $NURTURE_API_KEY"`;
        if (writes) s += ' \\\n  -H "Idempotency-Key: $(uuidgen)"';
        if (endpoint.body) s += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${endpoint.body}'`;
        return s;
    }
    if (lang === 'js') {
        return `const res = await fetch("${url}", {\n  method: "${endpoint.method}",\n  headers: {\n    Authorization: \`Bearer \${process.env.NURTURE_API_KEY}\`,${writes ? '\n    "Idempotency-Key": crypto.randomUUID(),' : ''}${endpoint.body ? '\n    "Content-Type": "application/json",' : ''}\n  },${endpoint.body ? `\n  body: JSON.stringify(${endpoint.body.replace(/\n/g, '\n  ')}),` : ''}\n});\nif (!res.ok) throw (await res.json()).error;`;
    }
    return `import os, uuid, requests\n\nres = requests.${endpoint.method.toLowerCase()}(\n    "${url}",\n    headers={\n        "Authorization": f"Bearer {os.environ['NURTURE_API_KEY']}",${writes ? '\n        "Idempotency-Key": str(uuid.uuid4()),' : ''}\n    },${endpoint.body ? `\n    json=${endpoint.body.replace(/true/g, 'True').replace(/\n/g, '\n    ')},` : ''}\n)\nres.raise_for_status()`;
}

function Code({ children, copyText }) {
    const copy = async () => {
        if (await copyToClipboard(copyText ?? children)) toast.success('Copied');
    };
    return (
        <div className="relative">
            <pre className="bg-stone-900 text-stone-200 rounded-lg px-4 py-3.5 font-mono text-xs leading-relaxed overflow-x-auto whitespace-pre">{children}</pre>
            <button type="button" onClick={copy} className="absolute top-2 right-2 h-6 px-2 rounded-md bg-stone-800 text-stone-300 text-[11.5px] inline-flex items-center gap-1.5 hover:bg-stone-700">
                <Copy className="w-3 h-3" /> Copy
            </button>
        </div>
    );
}

function Guide({ onPick }) {
    const upsert = ENDPOINTS.find((e) => e.id === 'contacts-upsert');
    const link = (id, label) => (
        <button type="button" className="text-violet-700 font-medium hover:underline" onClick={() => onPick(id)}>{label}</button>
    );
    return (
        <div className="border border-stone-200 rounded-xl p-5 flex flex-col gap-4" data-testid="docs-guide">
            <b className="text-[17px] font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Credit a teammate for a lead</b>
            <p className="text-sm text-stone-600 leading-relaxed max-w-[72ch]">
                This works the same for every app. Adding one never needs a change in NurtureCRM: an admin creates a key, and the app follows these steps.
            </p>
            <ol className="list-decimal pl-5 flex flex-col gap-2.5 text-sm text-stone-700 leading-relaxed">
                <li><b className="text-stone-900">Every teammate has a Referral ID</b> on their Profile, for example <span className="font-mono">owen-banan</span>. They share it with the people they refer.</li>
                <li><b className="text-stone-900">Your app collects it however suits you</b>: a <span className="font-mono">?ref=</span> link, a promo-code field, or a “Who referred you?” question.</li>
                <li><b className="text-stone-900">Send it with the lead</b> as <span className="font-mono">attribution.referred_by</span> on {link('contacts-create', 'POST /contacts')} or {link('contacts-upsert', '/contacts/upsert')}. A teammate’s email works too.</li>
                <li><b className="text-stone-900">NurtureCRM credits that teammate and makes them the owner</b> of a new lead. If no one matches, the lead is still created for the key’s default owner, with a warning.</li>
                <li><b className="text-stone-900">Optional:</b> send progress as {link('contacts-events', 'timeline events')}, look people up with {link('users-lookup', '/users/lookup')}, and hear about changes through {link('webhooks', 'webhooks')}.</li>
            </ol>
            <Code copyText={codeFor(upsert, 'curl')}>{codeFor(upsert, 'curl')}</Code>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead><tr className="text-left text-xs font-medium uppercase tracking-wider text-stone-500 border-b border-stone-200"><th className="px-3 py-2">attribution field</th><th className="px-3 py-2">Meaning</th></tr></thead>
                    <tbody className="text-[13px]">
                        {[
                            ['referred_by', 'Referral ID (current or older) or email of the teammate to credit. Required inside attribution.'],
                            ['campaign', 'Your own label, like oct-certification-drive'],
                            ['utm.source, utm.medium, utm.campaign, utm.content, utm.term', 'Saved on a new contact as utm_* fields'],
                            ['landing_url', 'The page the person signed up on'],
                        ].map(([f, m]) => (
                            <tr key={f} className="border-b border-stone-100 last:border-b-0"><td className="px-3 py-2 font-mono text-xs">{f}</td><td className="px-3 py-2">{m}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Note icon={Lock} title="The first credit sticks">
                Once a contact is credited, later referrals are recorded on its timeline but don’t move the credit, and an existing owner is never replaced. Only an admin can correct a credit.
            </Note>
        </div>
    );
}

function Errors() {
    return (
        <div className="border border-stone-200 rounded-xl p-5 flex flex-col gap-3.5" data-testid="docs-errors">
            <b className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Errors</b>
            <p className="text-sm text-stone-600">Every error has the same shape and carries the request_id you can search in the Request log. A write can also succeed with warnings, such as referrer_not_found.</p>
            <Code>{'{\n  "error": {\n    "code": "missing_scope",\n    "message": "This API key lacks the scope companies:create.",\n    "field": null,\n    "request_id": "6f1c…"\n  }\n}'}</Code>
            <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead><tr className="text-left text-xs font-medium uppercase tracking-wider text-stone-500 border-b border-stone-200"><th className="px-3 py-2">Status</th><th className="px-3 py-2">Code</th><th className="px-3 py-2">Meaning</th></tr></thead>
                    <tbody className="text-[13px]">
                        {ERRORS.map(([s, c, m]) => (
                            <tr key={c} className="border-b border-stone-100 last:border-b-0"><td className="px-3 py-2"><StatusCode status={s} /></td><td className="px-3 py-2 font-mono text-xs">{c}</td><td className="px-3 py-2">{m}</td></tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function DocsTab() {
    const [current, setCurrent] = useState('guide');
    const [lang, setLang] = useState('curl');
    const endpoint = ENDPOINTS.find((e) => e.id === current);
    const groups = [...new Set(ENDPOINTS.map((e) => e.group))];

    const navButton = (id, children) => (
        <button
            key={id}
            type="button"
            onClick={() => setCurrent(id)}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left text-[13px] ${current === id ? 'bg-violet-50 text-stone-900 font-semibold' : 'text-stone-700 hover:bg-stone-100'}`}
        >
            {children}
        </button>
    );

    return (
        <div className="grid lg:grid-cols-[250px_minmax(0,1fr)] gap-4 items-start" data-testid="docs-tab">
            <nav className="border border-stone-200 rounded-xl p-2 flex flex-col gap-0.5 lg:sticky lg:top-0">
                <div className="text-[10px] font-bold tracking-wider uppercase text-stone-500 px-2.5 pt-2.5 pb-1">Guides</div>
                {navButton('guide', <><Share2 className="w-3.5 h-3.5" />Referrals and attribution</>)}
                {groups.map((g) => (
                    <React.Fragment key={g}>
                        <div className="text-[10px] font-bold tracking-wider uppercase text-stone-500 px-2.5 pt-2.5 pb-1">{g}</div>
                        {ENDPOINTS.filter((e) => e.group === g).map((e) => navButton(e.id, <><Method method={e.method} /><span className="font-mono text-xs truncate">{e.path.split('?')[0]}</span></>))}
                    </React.Fragment>
                ))}
                <div className="text-[10px] font-bold tracking-wider uppercase text-stone-500 px-2.5 pt-2.5 pb-1">Reference</div>
                {navButton('webhooks', <><Webhook className="w-3.5 h-3.5" />Webhook events</>)}
                {navButton('errors', <><AlertTriangle className="w-3.5 h-3.5" />Errors</>)}
            </nav>
            <div className="min-w-0 flex flex-col gap-4">
                {current === 'guide' && <Guide onPick={setCurrent} />}
                {current === 'errors' && <Errors />}
                {current === 'webhooks' && <WebhookEvents />}
                {endpoint && (
                    <div className="border border-stone-200 rounded-xl p-5 flex flex-col gap-3.5" data-testid="docs-endpoint">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <Method method={endpoint.method} />
                            <span className="font-mono text-sm font-medium">{endpoint.path}</span>
                            <span className="flex-1" />
                            {endpoint.scopes.length
                                ? <><span className="text-[12.5px] text-stone-500">Needs</span>{endpoint.scopes.map((s) => <ScopeChip key={s} scope={s} />)}</>
                                : <span className="text-[12.5px] text-stone-500">Works with any key</span>}
                        </div>
                        <p className="text-sm text-stone-600 leading-relaxed max-w-[72ch]">{endpoint.description}</p>
                        {endpoint.params && (
                            <div className="border border-stone-200 rounded-lg overflow-hidden">
                                <table className="w-full">
                                    <thead><tr className="text-left text-xs font-medium uppercase tracking-wider text-stone-500 border-b border-stone-200"><th className="px-3 py-2">Query parameter</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Description</th></tr></thead>
                                    <tbody className="text-[13px]">
                                        {endpoint.params.map(([n, t, d]) => (
                                            <tr key={n} className="border-b border-stone-100 last:border-b-0"><td className="px-3 py-2 font-mono text-xs">{n}</td><td className="px-3 py-2 text-stone-500">{t}</td><td className="px-3 py-2">{d}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <b className="text-sm">Request</b>
                            <div className="flex gap-1">
                                {[['curl', 'cURL'], ['js', 'JavaScript'], ['py', 'Python']].map(([v, l]) => (
                                    <button key={v} type="button" onClick={() => setLang(v)} className={`h-7 px-2.5 rounded-md text-[12.5px] font-medium ${lang === v ? 'bg-stone-100 text-stone-900' : 'text-stone-600'}`}>{l}</button>
                                ))}
                            </div>
                        </div>
                        <Code>{codeFor(endpoint, lang)}</Code>
                        <b className="text-sm">Response</b>
                        <Code>{endpoint.response}</Code>
                    </div>
                )}
            </div>
        </div>
    );
}
